import { getLongFromShortCode } from '@homeaway/google-address-parser';
import StabAddressFormConfigurator from '@homeaway/stab-address-form-configurator';
// eslint-disable-next-line import/no-cycle
import LocationActions from '../actions/location';
import { genericFireAction } from '../middleware/validation/util';
import getLatLongErrorKey, { DUPLICATE_ADDRESS, ERROR_INFORMATION, MISSING_UNIT_NUMBER } from './ErrorUtil';
import log from './ClientLogger';

export const addressFields = (address, useLongCountryCode) => {
    const {
        country
    } = address;

    let countryValue = country;

    if (countryValue && useLongCountryCode) {
        countryValue = getLongFromShortCode(countryValue.toUpperCase());
    }

    return ({
        ...address,
        country: countryValue
    });
};

export const generateDefaultValidation = () => {
    const validationObject = {
        address1: {
            isSet: false,
            isLatin: true,
            isValid: false,
            error: undefined
        },
        address2: {
            isSet: false,
            isLatin: true,
            isValid: false,
            error: undefined
        },
        city: {
            isSet: false,
            isLatin: true,
            isValid: false,
            error: undefined
        },
        postalCode: {
            isSet: false,
            isLatin: true,
            isValid: false,
            error: undefined
        },
        country: {
            isSet: false,
            isLatin: true,
            isValid: false,
            error: undefined
        },
        stateProvince: {
            isSet: false,
            isLatin: true,
            isValid: false,
            error: undefined
        }
    };
    // return a fresh copy to break any potential references
    return JSON.parse(JSON.stringify(validationObject));
};

export const generateDefaultServerValidation = () => {
    const validationObject = {
        address1: {
            isValid: true,
            error: undefined,
            errorCode: ''
        },
        address2: {
            isValid: true,
            error: undefined
        },
        city: {
            isValid: true,
            error: undefined,
            errorCode: ''
        },
        postalCode: {
            isValid: true,
            error: undefined,
            errorCode: ''
        },
        country: {
            isValid: true,
            error: undefined,
            errorCode: ''
        },
        stateProvince: {
            isValid: true,
            error: undefined,
            errorCode: ''
        },
        generalState: {
            isValid: true,
            errorList: []
        },
        pinMap: {
            isValid: true,
            error: undefined,
            errorCode: ''
        }
    };
    // return a fresh copy to break any potential references
    return JSON.parse(JSON.stringify(validationObject));
};

// eslint-disable-next-line max-len
export const validateServerAddress = (translationUtil, address, currentValidation, {
    unitNumberValidation,
    validationErrorList,
    latLngError
}) => {
    const newValidation = currentValidation;
    const updateValue = (error, fieldToClear) => {
        if (!error && !fieldToClear) return;
        let key;
        let value = '';
        let errorCode = '';
        if (!error && fieldToClear) {
            key = fieldToClear;
        } else {
            const { location, code } = getLatLongErrorKey(error);
            key = location;
            value = translationUtil.getTranslation(ERROR_INFORMATION[code].error_translation);
            errorCode = code;
        }
        // if error indicates a duplicate address
        if (errorCode === DUPLICATE_ADDRESS) {
            const values = {};
            // address fields need to be sorted for translations to work
            Object.keys(address).sort().forEach((field) => {
                const fieldValue = address[field];
                if (values[fieldValue]) {
                    newValidation[field] = {
                        errorCode,
                        isValid: false,
                        error: translationUtil.getTranslation(`address.validation.error.duplicate.${values[fieldValue]}.${field}`)
                    };
                    newValidation[values[fieldValue]] = {
                        errorCode,
                        isValid: false,
                        error: translationUtil.getTranslation(`address.validation.error.duplicate.${values[fieldValue]}.${field}`)
                    };
                }
                // maps address value to field
                values[fieldValue] = field;
            });
            return;
        }
        if (newValidation[key].errorList) {
            // eslint-disable-next-line max-len
            if (value) value = Array.from(new Set([...newValidation[key].errorList, ...[errorCode]]));
            if (!value) value = [];
            newValidation[key].isValid = value.length === 0;
            newValidation[key].errorList = value;
        } else {
            newValidation[key] = {
                isValid: false,
                error: value,
                errorCode
            };
        }
    };

    const {
        missingUnitNumber,
        unitNumberValid
    } = unitNumberValidation;

    const validUN = !missingUnitNumber && unitNumberValid;
    // validate unit number
    newValidation.address2.isValid = validUN;
    newValidation.address2.error = '';
    // eslint-disable-next-line max-len
    if (missingUnitNumber) newValidation.address2.error = translationUtil.getTranslation(ERROR_INFORMATION[MISSING_UNIT_NUMBER].error_translation);
    // eslint-disable-next-line max-len
    if (!unitNumberValid) newValidation.address2.error = translationUtil.getTranslation(ERROR_INFORMATION[MISSING_UNIT_NUMBER].error_translation_2);

    updateValue(latLngError, 'generalState');

    validationErrorList.forEach((error) => { updateValue(error); });

    return newValidation;
};

export const validateFieldChangeServerAddress = (
    address,
    action,
    {
        missingUnitNumber,
        unitNumberValid,
        number: unitNumber
    },
    translationUtil,
    store
) => {
    // base status of a field should be valid if there is a value in it
    // geo validation will update the status of the field later on
    if (action.value) {
        genericFireAction(store, LocationActions.setServerAddresValidation, {
            [action.field]: {
                isValid: true,
                error: ''
            }
        });
    }
    // if the address is changing, the unit number validation should be cleared
    // or defaulted to false (not missing a unit number)
    if (action.field !== 'address2') {
        genericFireAction(store, LocationActions.validateUnitNumberClear);
    }
    if (
        // address2 field is updating with empty value and unit number is detected as missing
        (action.field === 'address2')
    ) {
        // no check for unitNumberValid because it is assumed that if the field changed,
        // a new validation should be run
        if (missingUnitNumber && !action.value) {
            // eslint-disable-next-line max-len
            genericFireAction(store, LocationActions.setServerAddresValidation, {
                address2: {
                    isValid: false,
                    error: translationUtil.getTranslation('Vrbo.lyp.manualLocation.validation.error.notEmpty.unit')
                }
            });
        } else if (action.value === unitNumber && !unitNumberValid) {
            // eslint-disable-next-line max-len
            genericFireAction(store, LocationActions.setServerAddresValidation, {
                address2: {
                    isValid: false,
                    error: translationUtil.getTranslation('address.validation.error.invalid.unitNumber')
                }
            });
        } else {
            genericFireAction(store, LocationActions.setServerAddresValidation, {
                address2: {
                    isValid: true,
                    error: ''
                }
            });
        }
    }
};

export const rebalanceString = (str, limit, splitter, joiner, subsequentLimit = null) => {
    const result = [''];
    const splitStr = str.split(splitter).reverse().filter((x) => !!x);
    let index = 0;
    let j = '';
    let currentLimit = limit;
    while (splitStr.length) {
        if (index > 0 && subsequentLimit !== null) currentLimit = subsequentLimit;
        const atom = splitStr.pop();
        if (atom.length > currentLimit) throw new Error(`Unable to split address. Atom (${atom}) is greater than the limit ${currentLimit}`);
        if (result[index].length + atom.length + joiner.length > currentLimit) {
            index += 1;
            j = '';
            if (subsequentLimit !== null) currentLimit = subsequentLimit;
        }
        if (result.length < index + 1) result.push('');
        result[index] = result[index] + j + atom;
        j = joiner;
    }
    return result;
};

const emptyIfNull = (str) => (!str ? '' : str);
const isOverLimit = (str, limit) => !!str && str.length > limit;
const isVrboAddress = (addr) => !!addr.address1 || !!addr.address2;
const isEgAddress = (addr) => !!addr.streetAddressLine1 || !!addr.streetAddressLine2;

const innerRebalanceAddr1AndAddr2 = (address, a1Key, a2Key) => {
    const result = { ...address };
    const a1 = result[a1Key];
    const a2 = result[a2Key];
    if (isOverLimit(a1, 200) || isOverLimit(a2, 50)) {
        const rawAddress = `${emptyIfNull(a1)} ${emptyIfNull(a2)}`;
        const newBalance = rebalanceString(rawAddress, 200, /[ ]/, ' ', 50);
        if (newBalance.length > 2) {
            // We couldn't balance the address across two lines, so give up.
            throw new Error('We could not balance the address1 and address2 within the defined limits (A1=200, A2=50).');
        }
        // eslint-disable-next-line prefer-destructuring
        result[a1Key] = newBalance[0] || '';
        result[a2Key] = newBalance[1] || '';
    }
    return result;
};

const getAddressKeys = (address) => {
    const isVrbo = isVrboAddress(address);
    const isEg = isEgAddress(address);
    let a1Key = 'streetAddressLine1';
    let a2Key = 'streetAddressLine2';
    if (isVrbo && isEg) {
        throw new Error('The address appears to contain VR and CL markers. This is dissallowed');
    } else if (isVrbo) {
        a1Key = 'address1';
        a2Key = 'address2';
    }
    // if we cannot find markers for EG or Vrbo,
    // then we will just return the object as is,
    // so no need to check for it.
    return ({
        a1Key,
        a2Key
    });
};

export const rebalanceAddr1AndAddr2 = (address) => {
    const { a1Key, a2Key } = getAddressKeys(address);
    return innerRebalanceAddr1AndAddr2(address, a1Key, a2Key);
};

const sortedAddress = (formConfig) => {
    const formParts = Object.keys(formConfig).map((key) => ({
        ...formConfig[key],
        fieldName: key
    }));
    formParts.sort((partA, partB) => partA.posIndex - partB.posIndex);
    return formParts;
};

function formattedAddress(formConfig, address) {
    const sortedFormParts = sortedAddress(formConfig);
    let formattedAddressString = '';
    sortedFormParts.forEach((formPart) => {
        const { fieldName } = formPart;
        const partValue = address[fieldName];
        const formattedParts = [formattedAddressString, partValue].filter(Boolean);
        formattedAddressString = formattedParts.join(', ');
    });
    return formattedAddressString;
}

export const getFormattedAddress = (address) => {
    const formConfig = StabAddressFormConfigurator.getTwoLineAddressfieldByCountry(address.country);
    const line1 = formattedAddress(formConfig.line1, address);
    const line2 = formattedAddress(formConfig.line2, address);

    return [line1, line2].filter(Boolean).join(', ');
};

export const getGoogleLatLng = ({
    address, candidateLatLng, propertyName, pathSource, minCoordinates
}, start, success, fail) => (dispatch) => {
    // Ensure tests that mock dispatch expecting thunks do not break when we send plain actions.
    const fire = (actionOrThunk) => {
        if (typeof actionOrThunk === 'function') {
            return dispatch(actionOrThunk);
        }
        return dispatch((inner) => inner(actionOrThunk));
    };
    try {
        fire(start());
        if (candidateLatLng && candidateLatLng.lat && candidateLatLng.lng) {
            fire(LocationActions.setLatLng(candidateLatLng));
            fire({
                type: LocationActions.SET_PARTIAL_MATCH,
                // eslint-disable-next-line camelcase
                partialMatch: false,
                persist: true
            });
            return fire(success({
                ...candidateLatLng,
                error: ''
            }));
        }
        const googleApi = window.google.maps;
        const geocoderAPI = new googleApi.Geocoder();

        const addressToUse = minCoordinates || getFormattedAddress(address);
        return geocoderAPI.geocode({
            address: addressToUse
        }, async (data, status) => {
            if (status !== 'OK') {
                log('error', 'Google latlng fetch', {
                    address,
                    pathSource,
                    propertyName,
                    error: status
                });
                return fire(fail(status));
            }
            // eslint-disable-next-line camelcase
            const { geometry, partial_match } = data[0];
            const latLng = {
                lat: geometry.location.lat(),
                lng: geometry.location.lng()
            };
            fire({
                type: LocationActions.SET_PARTIAL_MATCH,
                // eslint-disable-next-line camelcase
                partialMatch: !(!partial_match),
                persist: true
            });
            fire(LocationActions.setLatLng(latLng));
            return fire(success({
                ...latLng,
                error: ''
            }));
        });
    } catch (e) {
        log('error', 'Google latlng fetch', {
            address,
            pathSource,
            propertyName,
            error: e
        });
        return fire(fail(e));
    }
};

export default {
    addressFields,
    generateDefaultValidation,
    rebalanceAddr1AndAddr2,
    rebalanceString
};
